import "./Tooltip.css";

import { createPortal } from "react-dom";
import { Tooltip as ReactTooltip } from "react-tooltip";

import { tv, type VariantProps } from "tailwind-variants";

export const tooltipVariants = tv({
	slots: {
		base: "relative z-50 flex max-w-60 items-center justify-center overflow-hidden px-4 wrap-anywhere py-3 font-semibold shadow-lg transition-all duration-200 ease-in-out",
	},
	variants: {
		size: {
			xs: {
				base: "gap-1 rounded-sm text-xs",
			},
			sm: {
				base: "gap-1 rounded-md text-sm",
			},
			md: {
				base: "gap-2 rounded-lg text-base",
			},
			lg: {
				base: "gap-3 rounded-xl text-lg",
			},
			xl: {
				base: "gap-4 rounded-2xl text-2xl",
			},
		},
		color: {
			primary: {
				base: "border-primary bg-default text-primary outline-primary-active-ring",
			},
			secondary: {
				base: "border-secondary-border bg-secondary text-on-secondary outline-secondary-active-ring",
			},
			danger: {
				base: "outline-danger-active-ring",
			},
			info: {
				base: "outline-info-active-ring",
			},
			success: {
				base: "outline-success-active-ring",
			},
			warning: {
				base: "outline-warning-active-ring",
			},
		},
		variant: {
			solid: {
				base: "",
			},
			outline: {
				base: "border-2",
			},
		},
	},
});
export type TooltipVariants = VariantProps<typeof tooltipVariants>;

export type TooltipProps = TooltipVariants & {
	readonly children: React.ReactNode;
	readonly anchor: string;
	readonly openOnClick?: boolean | undefined;
	readonly arrow?: boolean | undefined;
	readonly defaultPlace?: "top" | "right" | "bottom" | "left" | undefined;
	readonly offset?: number | undefined;
	readonly afterShow?: () => void;
	readonly delayShow?: number;
	readonly className?: string;
	readonly hidden?: boolean;
};

export function Tooltip({
	children,
	anchor,
	openOnClick = false,
	arrow = true,
	defaultPlace = "top",
	offset,
	afterShow,
	className,
	delayShow = 100,
	color = "secondary",
	variant = "outline",
	size = "sm",
	hidden,
}: TooltipProps) {
	const { base } = tooltipVariants({ color, variant, size });

	const tooltipContent = (
		<ReactTooltip
			disableStyleInjection
			className={base({ className })}
			anchorSelect={`[data-tooltip='${anchor}']`}
			openOnClick={openOnClick}
			noArrow={!arrow}
			place={defaultPlace}
			offset={offset}
			afterShow={afterShow}
			delayShow={delayShow}
			globalCloseEvents={{
				escape: true,
				scroll: true,
				resize: true,
				clickOutsideAnchor: true,
			}}
		>
			{children}
		</ReactTooltip>
	);

	return !hidden && createPortal(tooltipContent, document.body);
}
